import { Button, Col, Form, Input, Row, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import { formGutter } from '../../lib/layout'
import { ibanValidator } from '../../lib/shared/validation/IbanValidator'
import _toUpper from 'lodash/toUpper'
import { useValidationI18n } from '../../lib/validation/useValidationI18n'

export default function CompanyBankingForm({
  sendFormValues,
  isFinalStep,
  loading,
  data,
  goBack,
  actionsClassName,
  onCancel,
}) {
  const { t } = useTranslation()
  const [form] = Form.useForm()

  async function handleFormValuesChange(values) {
    const iban = values.banking?.iban
    if (iban && iban.toUpperCase().startsWith('DE')) {
      form.setFieldsValue({
        banking: {
          bic: null,
          bank: null,
        },
      })
    }
  }

  async function handleSubmit(values) {
    const iban = _toUpper(values.banking.iban.replace(/\s/g, ''))
    const finalValues = {
      banking: {
        accountOwner: values.banking.accountOwner.trim(),
        iban: iban,
        bic: iban.startsWith('DE') ? null : values.banking.bic?.trim() || null,
        bank: iban.startsWith('DE')
          ? null
          : values.banking.bank?.trim() || null,
      },
    }

    sendFormValues(finalValues, isFinalStep, data?.uid)
  }

  const iban = Form.useWatch(['banking', 'iban'], {
    form,
    preserve: true,
  })

  const isGermanIBAN = iban?.toUpperCase()?.startsWith('DE')

  const initialValues = {
    banking: {
      ...data.banking,
      accountOwner: data.banking?.accountOwner || data.name,
    },
  }

  const i18n = useValidationI18n()

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onValuesChange={handleFormValuesChange}
      onFinish={handleSubmit}
    >
      <Row gutter={formGutter}>
        <Col xs={24} md={12}>
          <Form.Item
            label={t('frontend:company.accountOwner')}
            name={['banking', 'accountOwner']}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('frontend:company.required'),
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={t('frontend:company.iban')}
            name={['banking', 'iban']}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('frontend:company.required'),
              },
              {
                validator: ibanValidator(i18n),
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={t('frontend:company.bankName')}
            name={['banking', 'bank']}
            rules={[
              {
                required: !isGermanIBAN,
                whitespace: true,
                message: t('frontend:company.required'),
              },
            ]}
          >
            <Input disabled={isGermanIBAN} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={t('frontend:company.bicSwift')}
            name={['banking', 'bic']}
            rules={[
              {
                required: !isGermanIBAN,
                whitespace: true,
                message: t('frontend:company.required'),
              },
            ]}
          >
            <Input disabled={isGermanIBAN} />
          </Form.Item>
        </Col>
        <Col
          xs={24}
          style={{ textAlign: 'right' }}
          className={actionsClassName}
        >
          <Space>
            {onCancel && (
              <Button onClick={onCancel}>{t('frontend:common.cancel')}</Button>
            )}
            {goBack && (
              <Button type="text" onClick={() => goBack(null)}>
                {t('frontend:common.back')}
              </Button>
            )}
            <Button type="primary" htmlType="submit" loading={loading}>
              {isFinalStep
                ? t('frontend:company.save')
                : t('frontend:company.next')}
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  )
}
